<script type="text/ecmascript-6">
import GeneralMixin from "JS/mixins/campaign/general";
import {
  capitalize as _capitalize,
  difference as _difference,
  isEmpty as _isEmpty,
  forEach as _forEach
} from "lodash";
import { SlideYTransition, StampTransition } from "JS/transitions/transitions";
import Tooltip from "StyleguideComponents/Tooltip.vue";

export default {
  name: "Nav",
  components: {
    SlideYTransition,
    StampTransition,
    Tooltip
  },
  mixins: [GeneralMixin],
  props: {
    advancedAccessibility: {
      type: Boolean,
      default: false
    },
    advocateInfo: {
      required: true,
      type: Object
    },
    badgeDisplay: {
      required: true,
      type: Boolean
    },
    campaignDesignColors: {
      type: Object,
      default: () => ({})
    },
    actionButtonsText: {
      type: Object,
      default: null
    },
    fadeSuccessMessage: {
      type: Boolean,
      default: true
    },
    heading: {
      type: String,
      required: true
    },
    moveOnText: {
      required: true,
      type: String
    },
    navItems: {
      required: true,
      type: Object
    },
    tooltips: {
      required: true,
      type: Object
    },
    redirectUrl: {
      required: false,
      type: String,
      default: ""
    },
    redirectDelay: {
      required: false,
      type: String,
      default: "2"
    },
    successImages: {
      default: () => ({
        email: null,
        twitter: null,
        call: null
      }),
      type: Object
    },
    successMessages: {
      required: true,
      type: Object
    },
    tryAgainText: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      actionTwitter: "twitter",
      previouslyCompletedAction: null
    };
  },
  computed: {
    actionCompleted() {
      return this.$store.getters["navigation/actionCompleted"];
    },
    activeAction() {
      return this.$store.getters["navigation/activeAction"];
    },
    actionLoading() {
      return this.$store.getters["navigation/actionLoading"];
    },
    showSuccessBanner() {
      if (!this.showSuccessMessage) {
        return false;
      }

      return (
        !_isEmpty(this.successImage) ||
        (this.successMessage && this.noActionTaken) ||
        this.currentActionTaken ||
        this.allActionsAlreadyTaken
      );
    },
    showSuccessMessage() {
      return this.$store.getters["navigation/modalOpen"];
    },
    displayErrorModal() {
      return (
        this.currentAction === "twitter" &&
        this.actionError &&
        !this.completedAllActions
      );
    },
    actionSuccessModalHeader() {
      const actionCompletedDisplay =
        this.actionCompleted === "twitter" ? "tweet" : this.actionCompleted;
      return !_isEmpty(actionCompletedDisplay)
        ? `${_capitalize(actionCompletedDisplay)} Sent!`
        : "";
    },
    // Returns the correct nav class based on the number of enabled actions
    navWidthClass() {
      switch (this.enabledActions.length) {
        case 1:
          return "solo";
        case 2:
          return "halves";
        case 3:
          return "thirds";
        case 4:
          return "fourths";
        default:
          return "solo";
      }
    },
    successImage() {
      return this.successImages[this.previouslyCompletedAction] || false;
    },
    successMessage() {
      return this.successMessages[this.previouslyCompletedAction] || false;
    },
    allActionsAlreadyTaken() {
      return this.$store.getters["navigation/allActionsAlreadyTaken"];
    },
    takenActions() {
      return this.$store.getters["navigation/takenActions"];
    },
    showNav() {
      return !this.completedAllActions || this.allActionsAlreadyTaken;
    },
    noActionTaken() {
      return !this.currentActionTaken && !this.allActionsAlreadyTaken;
    },
    navTag() {
      return this.advancedAccessibility ? "div" : "nav";
    },
    navRole() {
      return this.advancedAccessibility ? false : "navigation";
    },
    hasTooltip() {
      return this.enabledActions.some(action => !!this.tooltips[action]);
    },
    hasActionText() {
      return Object.values(this.actionButtonsText).some(el => !!el);
    }
  },
  inject: {
    eventsBus: { from: "eventsBus" },
    redirectHelper: { from: "redirectHelper" }
  },
  watch: {
    actionCompleted() {
      this.handleActionCompleted();
    }
  },
  beforeMount() {
    this.eventsBus.$on("actionFailed", this.handleActionFailed);
    this.eventsBus.$on("skipAction", this.skipAction);
  },
  mounted() {
    if (this.advocateInfo && this.advocateInfo.actionsTaken) {
      this.checkTakenActions();
    }
  },
  methods: {
    /**
     * Set currently Active Action to the given action
     * @param action
     */
    activateAction(action) {
      if (!this.actionLoading) {
        this.$store.dispatch("forms/actionError", "");
        this.$store.dispatch("navigation/activeAction", action);
        this.eventsBus.$emit("actionActivated", action);
      }
    },
    checkTakenActions() {
      const actionsTaken = Object.values(this.advocateInfo.actionsTaken);

      let allActionsAlreadyTaken = false;

      if (this.activeAction === "default") {
        allActionsAlreadyTaken = this.advocateInfo.joinedCampaign;

        if (allActionsAlreadyTaken) {
          actionsTaken.push("default");
        }
      } else {
        allActionsAlreadyTaken =
          _difference(this.enabledActions, actionsTaken).length === 0;
      }

      this.handleAfterCheckTakenActions(actionsTaken, allActionsAlreadyTaken);
    },
    actionIcon(actionDetails, action) {
      let actionIconUrl =
        "https://s3.amazonaws.com/p2a-themes/conversational/assets/img/badges/";

      if (actionDetails[action] && !actionDetails[action].completed) {
        if (action === "email") {
          actionIconUrl += "email_badge_gray.svg";
        } else if (action === "twitter") {
          actionIconUrl += "twitter_badge_gray.svg";
        } else if (action === "call") {
          actionIconUrl += "call_badge_gray.svg";
        }
      } else if (action === "email") {
        actionIconUrl += "email_badge_color.svg";
      } else if (action === "twitter") {
        actionIconUrl += "twitter_badge_color.svg";
      } else if (action === "call") {
        actionIconUrl += "call_badge_color.svg";
      }

      return actionIconUrl;
    },
    handleAfterCheckTakenActions(actionsTaken, allActionsAlreadyTaken) {
      this.$store.dispatch(
        "navigation/allActionsAlreadyTaken",
        allActionsAlreadyTaken
      );

      // if all actions taken, also dispatch completedAllActions
      if (allActionsAlreadyTaken) {
        this.$store.dispatch("navigation/completedAllActions");

        if (this.badgeDisplay) {
          this.toggleSuccessMessage();
        }
      }

      // for each taken action, set it's detail to completed
      _forEach(actionsTaken, action => {
        if (this.actionDetails[action]) {
          this.actionDetails[action].completed = true;
        }
      });

      // when mount, check if first active action is taken and move to next action
      if (
        actionsTaken.indexOf(this.activeAction) > -1 &&
        !allActionsAlreadyTaken
      ) {
        this.actionDetails[this.activeAction].completed = true;
        this.goToNextAvailableAction();
      }
    },
    /**
     * Recursively compute the next available Action to switch to
     * in the navigation from the non-completed actions
     *
     * @param activeAction
     * @returns {string}
     */
    getNextAvailableAction(activeAction) {
      if (this.completedAllActions) {
        return "";
      }
      const nextCandidate = this.actionDetails[activeAction].nextAction;

      if (
        !this.actionDetails[nextCandidate].completed &&
        !this.actionDetails[nextCandidate].skipped
      ) {
        return nextCandidate;
      }
      return this.getNextAvailableAction(nextCandidate);
    },
    /**
     * Move on to make the next available action as the active action
     */
    goToNextAvailableAction() {
      this.$store.dispatch("navigation/actionLoading", false);
      this.$store.dispatch("forms/actionError", "");
      const nextAction = this.getNextAvailableAction(this.activeAction);

      if (nextAction.length) {
        this.activateAction(nextAction);
      }
    },
    skipAction() {
      const skippedValueForStore = {
        [this.activeAction]: {
          ...this.actionDetails[this.activeAction],
          skipped: true
        }
      };
      this.$store.dispatch("navigation/actionDetails", skippedValueForStore);

      this.setStepModalOpen();

      this.goToNextAvailableAction();
    },
    /**
     * Process the completed action event
     *
     */
    handleActionCompleted() {
      // This object updates the "completed" value for the current navigation action in the store
      const completedValueForStore = {
        [this.activeAction]: {
          ...this.actionDetails[this.activeAction],
          completed: true
        }
      };
      this.$store.dispatch("navigation/actionDetails", completedValueForStore);

      this.updateCompletedActions();

      // Redirect the user if all actions are completed and redirect is set
      if (this.numberOfIncompletedActions === 0 && this.redirectUrl) {
        this.handleRedirect(this.redirectUrl, this.redirectDelay);
        return;
      }

      this.previouslyCompletedAction = this.activeAction;
      if (this.previouslyCompletedAction && this.badgeDisplay) {
        this.setModalState(true);
        this.toggleSuccessMessage();
      }

      this.goToNextAvailableAction();
    },
    handleActionFailed(details) {
      if (details.action === this.actionTwitter) {
        this.$store.dispatch("forms/actionError", details.error);
      }
    },
    handleRedirect(redirectUrl, redirectDelay) {
      setTimeout(() => {
        this.redirectHelper.redirect(redirectUrl);
      }, redirectDelay * 1000 || 2000);
    },
    removeErrorMessage() {
      this.$store.dispatch("forms/actionError", "");
    },
    closeSuccessMessage() {
      this.setModalState(false);
    },
    toggleSuccessMessage() {
      this.setModalState(true);

      if (this.fadeSuccessMessage) {
        setTimeout(() => {
          this.setModalState(false);
        }, 6000); // note that this also affects time closing the banner in Aero
      }
    },
    updateCompletedActions() {
      if (this.numberOfIncompletedActions === 0) {
        this.$store.dispatch("navigation/completedAllActions");
      }
    },
    getThankYouMessage(successMessage, mergeVariables) {
      const $message = this.populateMergeVariables(successMessage, mergeVariables);

      return $message || '';
    }
  }
};
</script>
